import {AfterViewInit, Component, ElementRef, HostBinding, HostListener, NgZone, OnDestroy, ViewChild} from '@angular/core';
import {AsyncPipe, NgClass, NgIf} from '@angular/common';
import {CdkDrag, CdkDragHandle} from '@angular/cdk/drag-drop';
import {BehaviorSubject, combineLatest, distinctUntilChanged, interval, map, Observable, of, startWith, Subscription, switchMap} from 'rxjs';
import {CompactCallViewComponent} from '@shared/components/compact-call-view/compact-call-view.component';
import {
  CompactCallOption, CompactCallPerson, CompactCallSize, CompactCallViewModel
} from '@shared/components/compact-call-view/compact-call-view.model';
import {CallService, CallState} from '../call.service';
import {CallAudioDevices, CallAudioInUse, CallPhone, CallVoices} from '../call-media.port';
import {CallMediaRenderer} from '../call-media.renderer';
import {CallBarComponent} from '../call-bar/call-bar.component';
import {CallOverlayPosition} from '../call-overlay-position';
import {CallParticipants} from '../call-participants';

/** What the card draws: the call, and the compact view's model of it. */
interface VideoCallCardView {
  state: CallState;
  model: CompactCallViewModel;
}

/** How large the card is drawn (ITP-E-182-02): Small and Large where it was put, Full screen over the page. */
export type VideoCallCardSize = CompactCallSize;

/**
 * The video call card (ITP-E-182-01, the walking skeleton of the PRD's card): once anyone turned a camera on, every tab
 * that holds the call shows this card in place of the call bar — one overlay at a time — in the page the user is on.
 * The active speaker's video fills the stage, drawn by the call's own media client (no Meet join, no second client);
 * with no video on, the stage shows an avatar, and the card stays (D-05: a video call never turns back into the bar).
 * A tab that does not hold the call shows the bar's "On a call in another tab" instead, and never video.
 *
 * ITP-E-182-02 (D-08): everyone else is a thumbnail under the stage, a page at a time, so the SDK only ever draws the
 * speaker and one page. The card moves like the call bar (ITP-E-180-07): dragged by its name or moved with the arrow keys
 * on it, remembered across reloads, kept on screen. It minimizes to a slim strip and expands back; it is Small or Large
 * where it was put, or Full screen over the page — Esc returns, nothing navigates, the page stays underneath. The same in
 * every browser: no Document PiP (PiP stays Meet's).
 *
 * ITP-E-182-03: while anyone shares, their screen takes the big space and every speaker is a thumbnail; a strip under the
 * header says who shares and suggests Larger, Full screen and Open in Meet ↗. Share while someone else shares asks to take
 * over first.
 *
 * ITP-E-185-01 (D-09/D-11): the card is now only its shell — the drag, the header with Small/Large, Full screen and
 * Minimize, the share prompts — around the shared {@link CompactCallViewComponent} with Meet's PiP look: `card` mode, and
 * `strip` mode once minimized, the same component either way. The card builds the view's model from the call (who is on
 * the stage, everyone's video and voice, the group's Participants, what ⋯ offers) and hands it a {@link CallMediaRenderer},
 * so the view draws through the call's own media client. Participants, Devices, Record, Open in Meet and the host's End
 * call for everyone are in the view's ⋯.
 *
 * ITP-E-185-03 (D-11): the voice call is the same view's `strip` — the name and the timer, Mute, Camera, ⋯ and End call,
 * Participants, Devices, Share, Record, Open in Meet and End for everyone in ⋯ — so this shell holds every call the tab
 * holds, from the ring out to the hang-up, and the bar keeps the rest (the outcome, the passive bar, the blocked
 * microphone). Camera or Share converts the call and the very same component turns into the card, never remounted. The
 * voice strip sits, moves and is remembered where the call bar does (ITP-E-180-07), so its outcome shows where it was; the
 * card keeps its own place. Someone moving a group call to Meet is told here, with Join them in Meet.
 *
 * ITP-E-191-03: the strip is carbon's in-call mini bar — the face, the name over "mm:ss · N in call", the controls as
 * circles and Open call — and a minimized video call is that bar too: it sits, moves and is remembered where the voice
 * strip does, from the bottom centre (D-02), above the page and below its dialogs; Expand puts the card back in its own place.
 *
 * ITP-E-198-01: ⋯ offers Add by phone where the server says this user may (GetCallAccessCommand's CanAddPhone) and the
 * call's media account dials out. The phone people are rows of Participants, with Cancel or Hang up; a dial that did not
 * connect — busy, no answer, failed — is a short notice above the view that goes by itself.
 */
@Component({
  selector: 'app-video-call-card',
  standalone: true,
  imports: [NgIf, NgClass, AsyncPipe, CdkDrag, CdkDragHandle, CompactCallViewComponent],
  templateUrl: './video-call-card.component.html',
  styleUrls: ['./video-call-card.component.scss']
})
export class VideoCallCardComponent implements AfterViewInit, OnDestroy {
  /** Where the card was moved to, remembered across reloads (ITP-E-182-02). */
  static readonly positionKey = 'itp.calls.videoCallCard.position';

  /** The card's own place (ITP-E-182-02). */
  readonly cardOverlay = new CallOverlayPosition(VideoCallCardComponent.positionKey);
  /** The voice strip's place: the call bar's (ITP-E-180-07, ITP-E-185-03), shared with the bar's outcome and passive bar. */
  readonly stripOverlay = new CallOverlayPosition(CallBarComponent.positionKey);

  /** Draws the view's video through the call's own media client. */
  readonly renderer = new CallMediaRenderer(this.callService);

  /** Whose video is on the stage; null shows the avatar. */
  speaker: string | null = null;
  /** Who shares their screen now, this side included (ITP-E-182-03); null when nobody does. */
  sharer: string | null = null;
  size: VideoCallCardSize = 'small';
  minimized = false;
  /** A voice call this tab holds: the view is its strip, where the call bar is (ITP-E-185-03). */
  @HostBinding('class.video-call-card-voice')
  voice = false;
  /** The view is the strip — a voice call, or a minimized video call — and sits where the bar does (ITP-E-191-03). */
  @HostBinding('class.video-call-card-strip')
  strip = false;

  private videos: string[] = [];
  private voices: CallVoices = {};
  private shown = false;
  private devices: CallAudioDevices | null = null;
  /** The microphone and speaker in use, as the media reports them (ITP-E-189-12): the Devices panel's marks. */
  private audioInUse: CallAudioInUse = {microphoneId: null, speakerId: null};
  /** What Full screen goes back to. */
  private sizeBeforeFull: 'small' | 'large' = 'small';
  /** The card's own changes — its size, the strip, the devices — drawn without waiting for the call to change. */
  private readonly changed = new BehaviorSubject<void>(undefined);
  private readonly media: Subscription;
  /** Whether this user may add a phone person (ITP-E-198-01), as the server last said. */
  private canAddPhone = false;
  /** The phone people in the call and the dials on their way (ITP-E-198-01). */
  private phones: CallPhone[] = [];
  private readonly phoneSeen: Subscription;
  /** What the last dial that did not connect said (ITP-E-198-01), shown for a moment; null when nothing. */
  phoneNotice: string | null = null;
  private phoneNoticeTimer: any = null;
  /** How long a phone notice shows. */
  static readonly phoneNoticeMs = 6000;

  readonly view$: Observable<VideoCallCardView | null> = combineLatest([
    this.callService.state$,
    interval(1000).pipe(startWith(0)),
    this.changed
  ]).pipe(map(([state]) => CallService.showsCallView(state) ? {state, model: this.model(state, Date.now())} : null));

  /** Everything the card shows, moved as one (ITP-E-182-02). */
  @ViewChild('overlayElement', {static: true})
  private overlayElement!: ElementRef<HTMLElement>;

  constructor(private callService: CallService, private zone: NgZone, private host: ElementRef<HTMLElement>) {
    this.media = combineLatest([
      this.callService.state$, this.callService.videos$, this.callService.voices$, this.callService.sharer$, this.callService.audioInUse$
    ])
      .subscribe(([state, videos, voices, sharer, audioInUse]) => {
        this.audioInUse = audioInUse ?? {microphoneId: null, speakerId: null};
        this.shown = CallService.showsCard(state);
        if (!this.shown && this.size === 'full') {
          this.leaveFullScreen(false);
        }
        if (!this.shown) {
          this.minimized = false; // the next video call opens the card whole
        }
        this.voice = CallService.showsCallView(state) && !this.shown;
        this.place();
        this.videos = videos ?? [];
        this.voices = voices ?? {};
        this.speaker = this.shown
          ? VideoCallCardComponent.speaker(this.videos, this.voices, this.callService.selfMediaIdentity, this.speaker)
          : null;
        const shows = this.shown ? sharer ?? null : null;
        if (shows !== this.sharer) {
          console.debug('[calls] share the card shows', {sharer: shows, self: shows === this.callService.selfMediaIdentity, card: this.shown});
        }
        this.sharer = shows;
        this.changed.next();
      });
    // Asked once a call is connected, not before: a tab that never connects a call never asks.
    const canAddPhone$ = this.callService.state$.pipe(
      map(state => state.phase === 'connected'),
      distinctUntilChanged(),
      switchMap(connected => connected ? this.callService.canAddPhone$ : of(false))
    );
    this.phoneSeen = combineLatest([canAddPhone$, this.callService.phones$]).subscribe(([canAddPhone, phones]) => {
      this.canAddPhone = canAddPhone;
      this.phones = phones ?? [];
      this.changed.next();
    });
    this.phoneSeen.add(this.callService.phoneNotices$.subscribe(notice => this.showPhoneNotice(notice)));
  }

  /** Where the view sits and moves now: the call bar's place for the strip, the card's own for the card. */
  get overlay(): CallOverlayPosition {
    return this.strip ? this.stripOverlay : this.cardOverlay;
  }

  /** Who shares, as the share strip names them. */
  get sharerName(): string {
    return this.sharer ? this.nameOf(this.sharer) : '';
  }

  /** " · N in call" after the strip's timer (ITP-E-191-03). */
  inCallLabel(count: number): string {
    return CompactCallViewComponent.inCallLabel(count);
  }

  /** Whether this side is the one sharing (ITP-E-182-03). */
  get sharingHere(): boolean {
    return !!this.sharer && this.sharer === this.callService.selfMediaIdentity;
  }

  ngAfterViewInit(): void {
    this.overlay.attach(this.overlayElement.nativeElement, this.zone);
  }

  ngOnDestroy(): void {
    this.media.unsubscribe();
    this.phoneSeen.unsubscribe();
    this.dismissPhoneNotice();
    this.cardOverlay.detach();
    this.stripOverlay.detach();
    this.renderer.dispose().then();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.size !== 'full') {
      this.overlay.keepInView();
    }
  }

  /** Esc leaves Full screen, back to the size it came from; anywhere else it is not the card's. */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (this.size === 'full' && this.shown) {
      event.preventDefault();
      this.leaveFullScreen(true);
    }
  }

  onToggleMute(): void {
    this.callService.toggleMute().then();
  }

  /** Camera on or off in the session; the call stays a video call either way (D-05). */
  onToggleCamera(): void {
    this.callService.toggleCamera().then();
  }

  onHangUp(): void {
    this.callService.hangUp().then();
  }

  /** This side's screen on or off (ITP-E-182-03); while someone else shares it asks to take over first. */
  onToggleShare(): void {
    this.callService.toggleShare().then();
  }

  onTakeOverShare(): void {
    this.callService.takeOverShare().then();
  }

  onCancelShareTakeOver(): void {
    this.callService.cancelShareTakeOver();
  }

  onDismissShareNotice(): void {
    this.callService.dismissShareNotice();
  }

  /** Turn on sound (ITP-E-189-02): the click the browser waited for before it would play the call. */
  onEnableAudio(): void {
    this.callService.enableAudio().then();
  }

  onDismissMicFailed(): void {
    this.callService.dismissMicFailed();
  }

  /**
   * Open in Meet ↗ (ITP-E-182-05, for anyone): the window opens inside the click, so a popup blocker lets it through, and
   * the service sends it to the call's Meet room, which joins the call and takes it over from this tab (ITP-E-185-05).
   */
  onOpenInMeet(): void {
    const room = window.open('', '_blank');
    this.callService.openInMeet(room).then();
  }

  onDismissMovedToMeet(): void {
    this.callService.dismissMovedToMeet();
  }

  /** A phone notice's × (ITP-E-198-01); it also goes by itself. */
  dismissPhoneNotice(): void {
    if (this.phoneNoticeTimer) {
      clearTimeout(this.phoneNoticeTimer);
      this.phoneNoticeTimer = null;
    }
    this.phoneNotice = null;
  }

  private showPhoneNotice(notice: string): void {
    this.dismissPhoneNotice();
    this.phoneNotice = notice;
    this.phoneNoticeTimer = setTimeout(() => {
      this.phoneNoticeTimer = null;
      this.phoneNotice = null;
      this.changed.next();
    }, VideoCallCardComponent.phoneNoticeMs);
    this.changed.next();
  }

  /** What the view's ⋯ asked for. */
  onOption(option: CompactCallOption): void {
    switch (option.type) {
      case 'open-in-meet':
        this.onOpenInMeet();
        break;
      case 'end-for-all':
        this.callService.endForAll().then();
        break;
      case 'record':
        this.callService.requestRecording().then();
        break;
      case 'stop-recording':
        this.callService.stopRecording().then();
        break;
      case 'devices':
        this.callService.listDevices().then(devices => {
          this.devices = devices;
          this.changed.next();
        });
        break;
      // The mark follows the media's answer (audioInUse$), not the pick (ITP-E-189-12): a switch the SDK refused shows no change.
      case 'microphone':
        this.callService.selectMicrophone(option.deviceId).then();
        break;
      case 'speaker':
        this.callService.selectSpeaker(option.deviceId).then();
        break;
      case 'add-phone':
        this.callService.invitePhone(option.invite).then();
        break;
      case 'hang-up-phone':
        this.callService.hangUpPhone(option.key).then();
        break;
    }
  }

  /** Small to Large and back, where the card was put. */
  onToggleSize(): void {
    this.size = this.size === 'large' ? 'small' : 'large';
    this.changed.next();
  }

  /** Full screen over the page; the drag rests meanwhile and the card comes back where it was. */
  onToggleFullScreen(): void {
    if (this.size === 'full') {
      this.leaveFullScreen(true);
      return;
    }
    this.sizeBeforeFull = this.size;
    this.size = 'full';
    this.overlay.detach(); // the full-screen card fills the viewport: nothing to keep in view, nothing to move
    this.changed.next();
    this.focusLater('.video-call-card-fullscreen');
  }

  /** The slim strip — the same view, in strip mode; from Full screen, back to the size it came from first. */
  onMinimize(): void {
    if (this.size === 'full') {
      this.leaveFullScreen(false);
    }
    this.minimized = true;
    this.place();
    this.changed.next();
    this.focusLater('.compact-call-view-expand');
  }

  onExpand(): void {
    this.minimized = false;
    this.place();
    this.changed.next();
    this.focusLater('.video-call-card-minimize');
  }

  /**
   * Moves the view between the strip's place and the card's as the call turns from one to the other, or the card is
   * minimized or expanded. The view itself stays: only where it sits changes. The strip takes the bar's place as it was
   * last left — the bar may have moved it.
   */
  private place(): void {
    const strip = this.voice || this.minimized;
    if (strip === this.strip) {
      return;
    }
    this.overlay.detach();
    this.strip = strip;
    if (strip) {
      this.stripOverlay.refresh();
    }
    if (this.overlayElement && this.size !== 'full') {
      this.overlay.attach(this.overlayElement.nativeElement, this.zone);
    }
  }

  private leaveFullScreen(focus: boolean): void {
    this.size = this.sizeBeforeFull;
    this.overlay.attach(this.overlayElement.nativeElement, this.zone);
    this.changed.next();
    if (focus) {
      this.focusLater('.video-call-card-fullscreen');
    }
  }

  /** The compact view's model of the call as it stands: the strip of a voice call, the card (or its strip) of a video call. */
  private model(state: CallState, now: number): CompactCallViewModel {
    const self = this.callService.selfMediaIdentity;
    const voice = !CallService.showsCard(state);
    const identities = [...Object.keys(this.voices), ...this.videos].filter((identity, i, all) => identity && all.indexOf(identity) === i);
    const stage = voice ? null : this.stageIdentity();
    const sharer = voice ? null : this.sharer;
    // Add by phone (ITP-E-198-01): this user may, and the call's media dials out — known once it joined.
    const addPhone = this.canAddPhone && state.phase === 'connected' && this.callService.phoneSupported;
    return {
      mode: voice || this.minimized ? 'strip' : 'card',
      size: this.size,
      title: VideoCallCardComponent.title(state),
      timer: CallBarComponent.label(state, now),
      inCall: VideoCallCardComponent.inCall(state, identities) + this.phones.filter(phone => phone.status === 'in-call').length,
      video: !voice,
      self: {
        connected: state.phase === 'connected',
        muted: !!state.muted,
        videoOn: !!state.videoOn,
        sharing: !!state.sharing,
        canShare: this.callService.canShare
      },
      people: identities.map(identity => this.person(identity, self)),
      stage: stage ? this.person(stage, self) : null,
      sharer: sharer ? {identity: sharer, name: this.nameOf(sharer), self: sharer === self} : null,
      participants: CallParticipants.of(state, this.voices, now, this.phones, self),
      phone: addPhone ? {countries: this.callService.phoneCountries} : null,
      options: {
        openInMeet: CallService.opensInMeet(state),
        meetReady: state.phase === 'connected' && !!state.meetingId,
        record: !!state.isCaller,
        recording: state.recording?.status === 'Requested' || state.recording?.status === 'Recording',
        // ITP-E-198-04 (D-05): a phone person in the call, or being dialled, hears no recording announced.
        recordHeldBack: this.phones.length ? CallService.recordingNotWithPhone : null,
        endForAll: CallService.endsForAll(state),
        addPhone
      },
      devices: {list: this.devices, ...this.audioInUse},
      hangUpTitle: CallBarComponent.hangUpTitle(state),
      expandable: !voice
    };
  }

  private person(identity: string, self: string | null): CompactCallPerson {
    const name = this.nameOf(identity);
    return {
      identity,
      name,
      initials: CompactCallViewComponent.initials(identity === self ? this.selfName() : name),
      video: this.videos.includes(identity),
      muted: !!this.voices[identity]?.muted,
      speaking: !!this.voices[identity]?.speaking,
      self: identity === self
    };
  }

  /** Who the stage stands for: the speaker with video, else the other person speaking, else the other person. */
  private stageIdentity(): string | null {
    if (this.speaker) {
      return this.speaker;
    }
    const self = this.callService.selfMediaIdentity;
    const others = Object.keys(this.voices).filter(identity => identity !== self);
    const state = this.callService.state;
    return others.find(identity => this.voices[identity]?.speaking)
      ?? (state.isGroup ? null : others.find(identity => identity === state.peerEmail?.toLowerCase()) ?? others[0])
      ?? null;
  }

  /** A person's name as the card shows it: You, a group member's name, the other person's, or their email's name. */
  private nameOf(identity: string): string {
    const state = this.callService.state;
    if (identity === this.callService.selfMediaIdentity) {
      return 'You';
    }
    const member = state.participants?.find(participant => participant.email?.toLowerCase() === identity)?.name;
    if (member) {
      return member;
    }
    if (!state.isGroup && state.peerName) {
      return state.peerName; // a 1:1 call has one other person
    }
    return identity.replace(/@.*/, '');
  }

  private selfName(): string {
    const self = this.callService.selfMediaIdentity ?? '';
    return this.callService.state.participants?.find(participant => participant.email?.toLowerCase() === self)?.name || self;
  }

  /** Moves the focus to the control that took the place of the one pressed, once Angular has drawn it. */
  private focusLater(selector: string): void {
    setTimeout(() => (this.host.nativeElement.querySelector(selector) as HTMLElement | null)?.focus());
  }

  /**
   * How many people are in the call (ITP-E-191-03): everyone the media hears or sees, this side included, or a group's
   * Joined participants where the media has not caught up with them yet.
   */
  static inCall(state: CallState, identities: string[]): number {
    return Math.max(identities.length, CallService.inCallCountOf(state.participants));
  }

  /** The call's name: the other person, or the group's title. */
  static title(state: CallState): string {
    return (state.isGroup ? state.title : state.peerName) || '';
  }

  /**
   * Whose video fills the stage: someone else with video on who is speaking, else whoever is there now if still on, else
   * anyone else with video on, else this side's own. Null when nobody has video on: the avatar.
   */
  static speaker(videos: string[], voices: CallVoices, self: string | null, current: string | null): string | null {
    if (!videos.length) {
      return null;
    }
    const others = videos.filter(identity => identity !== self);
    const speaking = others.filter(identity => voices[identity]?.speaking);
    if (current && current !== self && others.includes(current) && (!speaking.length || speaking.includes(current))) {
      return current;
    }
    return speaking[0] ?? others[0] ?? videos[0];
  }
}
